import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import ActionButtons from 'components/SurveyResult/ActionButtons';
import AssessmentResultContext from 'containers/AssessmentResult/AssessmentResultContext';
import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import _capitalize from 'lodash/capitalize';
import _toLower from 'lodash/toLower';
import useSiteCopySelector from 'components/useSiteCopySelector';
import MixPanel, { injectAssessmentResultsABData } from 'utils/mixpanelService';
import { downloadPagePdf } from 'components/SurveyResult/utils';
import DownloadIcon from './images/download.svg';
// eslint-disable-next-line import/no-cycle
import CustomEmailResultsButton from './CustomEmailResultsButton';

// Custom styles for BSC buttons
const useCustomStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: props => (props.isVariantB ? 50 : 20),
    marginBottom: props => (props.isVariantB ? 0 : 20),
    paddingBottom: props => (props.isVariantB ? 32 : 0),
    paddingLeft: 0,
    borderBottom: props => (props.isVariantB ? '1px solid #E9ECED' : 'none'),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    listStyleType: 'none',
    '& li:first-child': {
      marginRight: 'auto',
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
      },
    },
  },
  assessmentsButton: {
    display: 'flex',
    padding: '16px 32px',
    borderRadius: 32,
    justifyContent: 'center',
    marginLeft: 8,
    marginRight: 8,
    border: `1px solid ${theme.palette.primary.main}`,
    fontWeight: 600,
    backgroundColor: theme.palette.primary.main,
    fontSize: 16,
    '&:hover': {
      backgroundColor: theme.palette.bgColor.default,
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
      width: '100%',
      marginTop: 16,
    },
  },
  emailButton: {
    display: 'flex',
    padding: '16px 32px',
    borderRadius: 32,
    justifyContent: 'center',
    marginLeft: 8,
    marginRight: 8,
    backgroundColor: theme.palette.primary.main,
    color: 'white !important',
    border: `1px solid ${theme.palette.primary.main}`,
    fontWeight: 600,
    fontSize: 16,
    textTransform: 'none !important',
    '&:hover': {
      backgroundColor: theme.palette.bgColor.default,
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
      width: '100%',
      marginTop: 16,
      minWidth: 225,
    },
    '& .MuiButton-label': {
      color: 'white !important',
      textTransform: 'none !important',
    },
    '& .MuiButton-root': {
      textTransform: 'none !important',
    },
  },
  textLink: {
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    textTransform: 'none',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 16,
    padding: 0,
    minWidth: 0,
    cursor: 'pointer',
    boxShadow: 'none',
    '&:hover': {
      color: theme.palette.bgColor.default,
      background: 'none',
      textDecoration: 'underline',
    },
  },
  primaryButton: {
    backgroundColor: '#0071BC !important',
    color: 'white !important',
    border: `1px solid ${theme.palette.primary.main} !important`,
    '&:hover': {
      backgroundColor: theme.palette.bgColor.default,
    },
    '& .MuiButton-label': {
      color: 'white !important',
    },
  },
  downloadButton: {
    borderRadius: 32,
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: 16,
    padding: '16px 32px',
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    marginRight: 24,
    '&:hover': {
      backgroundColor: theme.palette.bgColor.default,
      border: `1px solid ${theme.palette.bgColor.default}`,
      color: theme.palette.text.tertiary,
      '& img': {
        filter: 'brightness(0) saturate(100%) invert(100%)',
      },
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginRight: 0,
      marginTop: 16,
      width: 225,
    },
  },
  downloadIcon: {
    width: 20,
    height: 20,
    marginRight: 8,
    filter:
      'brightness(0) saturate(100%) invert(29%) sepia(95%) saturate(1267%) hue-rotate(182deg) brightness(91%) contrast(101%)',
  },
}));

const formatButtonText = text => {
  if (typeof text !== 'string') return text;
  return _capitalize(_toLower(text));
};

// Custom BSC Download Button component
const BSCDownloadButton = ({ answer, handlePdfContent }) => {
  const { isVariantB, shouldABTest } = useContext(AssessmentResultContext);
  const customClasses = useCustomStyles({ isVariantB });
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const { slug } = useParams();

  const assessmentID = _get(answer, 'assessment.assessmentID', '');
  const name = _get(answer, 'assessment.name', '');

  const handlePdfDownload = async () => {
    handlePdfContent(true);
    MixPanel.track(
      'Download PDF',
      injectAssessmentResultsABData(
        {
          path: window.location.pathname,
          type: 'assessment',
          slug,
          name,
          assessmentId: assessmentID,
          overallResultId: _get(answer, 'resultID.overall', null),
          source: 'action-buttons',
        },
        shouldABTest,
        isVariantB,
      ),
    );
    await downloadPagePdf(slug);
    handlePdfContent(false);
  };

  return (
    <Button
      variant="outlined"
      className={customClasses.downloadButton}
      onClick={handlePdfDownload}
    >
      <img src={DownloadIcon} alt="" className={customClasses.downloadIcon} />
      {formatButtonText(
        _get(assessmentResultsSiteCopy, 'pageCopy.downloadResultsLabel'),
      )}
    </Button>
  );
};

// Custom BSC Retake Button component
const BSCRetakeButton = ({ assessment }) => {
  const { isVariantB, shouldABTest } = useContext(AssessmentResultContext);
  const customClasses = useCustomStyles({ isVariantB });
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const { slug } = useParams();

  const assessmentID = _get(assessment, 'sys.id', '');
  const name = _get(assessment, 'fields.name', '');

  const handleRetakeClick = () => {
    MixPanel.track(
      'Assessment Results - Retake - Clicked',
      injectAssessmentResultsABData(
        {
          slug,
          assessmentName: name,
          assessmentId: assessmentID,
          placement: 'bottom',
          page: window.location.pathname,
          path: 'assessment results',
          type: 'bottom cta',
        },
        shouldABTest,
        isVariantB,
      ),
    );
    window.location.href = `/assessments/${slug}`;
  };

  return (
    <Button
      variant="text"
      className={customClasses.textLink}
      onClick={handleRetakeClick}
    >
      {formatButtonText(
        _get(assessmentResultsSiteCopy, 'pageCopy.retakeLabel', 'Retake'),
      )}
    </Button>
  );
};

const CustomActionButtons = props => {
  const { isVariantB } = useContext(AssessmentResultContext);
  const customClasses = useCustomStyles({ isVariantB });

  // Component overrides for ActionButtons
  const uiOverrides = {
    // Override container styles
    containerClassName: customClasses.container,

    // Override button styles
    assessmentsButtonClassName: customClasses.assessmentsButton,

    // BSC-specific button order: Retake on left, Download and Email on right
    buttonOrder: ['retake', 'download', 'email', 'certificate', 'clinical'],
  };

  // Create custom elements to completely replace default buttons
  const customElements = {
    download: (
      <BSCDownloadButton
        answer={props.answer}
        handlePdfContent={props.handlePdfContent}
      />
    ),
    email: (
      <CustomEmailResultsButton
        assessment={props.assessment}
        onEmailSent={props.handleEmailResults}
      />
    ),
    retake: <BSCRetakeButton assessment={props.assessment} />,
  };

  return (
    <ActionButtons
      {...props}
      uiOverrides={uiOverrides}
      customElements={customElements}
    />
  );
};

export default CustomActionButtons;
